import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
    Users,
    FolderKanban,
    Sparkles,
    DatabaseZap,
    RefreshCw,
    Trash2,
    ShieldCheck,
    Activity,
    ShieldAlert
} from "lucide-react";
import {
    getDashboardStats,
    clearGeneratedProjectsDryRun,
    clearGeneratedProjects
} from "../../services/crmService";

import UnclaimedPreviewSessionsPanel from "../../components/crm/UnclaimedPreviewSessionsPanel";

function AdminMetric({ icon: Icon, label, value, hint, to }) {
    const content = (
        <>
            <div className="dash-metric-icon">
                <Icon size={20} />
            </div>
            <span>{label}</span>
            <strong>{value || 0}</strong>
            <small>{hint}</small>
        </>
    );

    if (to) {
        return (
            <Link to={to} className="dash-metric-card clickable">
                {content}
            </Link>
        );
    }

    return <div className="dash-metric-card">{content}</div>;
}

function AdminSection({ kicker, title, description, action, children }) {
    return (
        <section className="dash-panel">
            <div className="dash-panel-head">
                <div>
                    <span className="project-kicker">{kicker}</span>
                    <h3>{title}</h3>
                    {description && <p>{description}</p>}
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}

function parseCleanupOrphanSlugs(value) {
    const raw = String(value || "").trim();

    if (!raw) return [];

    try {
        const parsed = JSON.parse(raw);

        if (Array.isArray(parsed?.orphanSlugs)) {
            return parsed.orphanSlugs
                .map((x) => String(x || "").trim())
                .filter(Boolean);
        }
    } catch {
        // Continue with plain text parsing.
    }

    return raw
        .split(/[\n,]+/)
        .map((x) => x.trim())
        .filter(Boolean)
        .filter((x) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(x));
}

export default function SuperAdminDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [cleanupScope, setCleanupScope] = useState("seed-test-only");
    const [cleanupIncludeClientOwned, setCleanupIncludeClientOwned] = useState(false);
    const [cleanupDeleteAzure, setCleanupDeleteAzure] = useState(true);
    const [cleanupDeleteLocal, setCleanupDeleteLocal] = useState(true);
    const [cleanupConfirmation, setCleanupConfirmation] = useState("");
    const [cleanupPreview, setCleanupPreview] = useState(null);
    const [cleanupRunning, setCleanupRunning] = useState(false);
    const [cleanupMessage, setCleanupMessage] = useState("");
    const [cleanupOrphanSlugs, setCleanupOrphanSlugs] = useState("");

    const load = async () => {
        try {
            setLoading(true);
            setError("");

            const data = await getDashboardStats();
            setStats(data);
        } catch (err) {
            console.error(err);
            setError("Dashboard stats could not be loaded.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const summaryText = useMemo(() => {
        if (loading) return "Loading platform status...";
        return "Monitor platform activity, clients, projects, AI usage, previews, and cleanup operations from one workspace.";
    }, [loading]);

    const handleCleanupDryRun = async () => {
        try {
            setCleanupRunning(true);
            setCleanupMessage("");
            setError("");

            const orphanSlugs = parseCleanupOrphanSlugs(cleanupOrphanSlugs);

            const result = await clearGeneratedProjectsDryRun({
                scope: cleanupScope,
                deleteAzureFiles: cleanupDeleteAzure,
                deleteLocalFiles: cleanupDeleteLocal,
                includeClientOwnedProjects: cleanupIncludeClientOwned,
                orphanSlugs
            });

            setCleanupPreview(result);
            setCleanupMessage(`Dry run complete. ${result.projectsMatched || 0} projects matched.`);
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || "Failed to run cleanup preview.");
        } finally {
            setCleanupRunning(false);
        }
    };

    const handleClearGeneratedProjects = async () => {
        if (cleanupConfirmation !== "CLEAR GENERATED PROJECTS") {
            setError("Type CLEAR GENERATED PROJECTS before deleting.");
            return;
        }

        const ok = window.confirm(
            "This will delete matching projects from database and generated site folders. This cannot be undone. Continue?"
        );

        if (!ok) return;

        try {
            setCleanupRunning(true);
            setCleanupMessage("");
            setError("");

            const orphanSlugs = parseCleanupOrphanSlugs(cleanupOrphanSlugs);

            const result = await clearGeneratedProjects({
                scope: cleanupScope,
                confirmationText: cleanupConfirmation,
                deleteAzureFiles: cleanupDeleteAzure,
                deleteLocalFiles: cleanupDeleteLocal,
                includeClientOwnedProjects: cleanupIncludeClientOwned,
                continueOnAzureFailure: true,
                orphanSlugs
            });

            setCleanupPreview(result);
            setCleanupMessage(result.message || "Generated projects cleanup completed.");

            await load();
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || "Failed to clear generated projects.");
        } finally {
            setCleanupRunning(false);
        }
    };

    return (
        <div className="dash-shell">
            <section className="dash-hero-card">
                <div>
                    <span className="project-kicker">Super Admin Command Center</span>
                    <h2>Platform Overview</h2>
                    <p>{summaryText}</p>
                </div>

                <button
                    type="button"
                    className="project-action-btn secondary"
                    onClick={load}
                    disabled={loading}
                >
                    <RefreshCw size={16} />
                    {loading ? "Refreshing..." : "Refresh"}
                </button>
            </section>

            <section className="dash-metric-grid">
                <AdminMetric
                    icon={Users}
                    label="Accounts"
                    value={stats?.totalAccounts}
                    hint="Client workspaces"
                    to="/accounts"
                />

                <AdminMetric
                    icon={FolderKanban}
                    label="Projects"
                    value={stats?.totalProjects}
                    hint="Generated sites"
                    to="/projects"
                />

                <AdminMetric
                    icon={Sparkles}
                    label="AI Usage"
                    value={stats?.aiCalls}
                    hint="Tracked AI calls"
                />

                <AdminMetric
                    icon={DatabaseZap}
                    label="Active Seeds"
                    value={stats?.activeSeeds}
                    hint="Vertical intelligence"
                    to="/vertical-seeds"
                />
            </section>

            {error && (
                <div className="dash-alert error">
                    <strong>Attention needed</strong>
                    <p>{error}</p>
                </div>
            )}

            <section className="dash-admin-grid">
                <AdminSection
                    kicker="Generation Control"
                    title="Vertical Intelligence Center"
                    description="Manage seeds, blueprints, seed learning, image materialization, test lab, and Seed ↔ Blueprint mapping from the dedicated workspace."
                    action={
                        <Link to="/vertical-seeds" className="project-action-btn primary">
                            Open Intelligence Center
                        </Link>
                    }
                >
                    <div className="dash-rec-list">
                        <Link to="/vertical-seeds" className="dash-rec-item">
                            <div className="dash-rec-top">
                                <strong>Seeds + Blueprints</strong>
                                <span className="seed-priority high">V2</span>
                            </div>
                            <p>
                                Review active seeds, blueprint registry, module alignment, image readiness, and generation test coverage.
                            </p>
                        </Link>

                        <Link to="/vertical-seeds" className="dash-rec-item">
                            <div className="dash-rec-top">
                                <strong>Learning + Test Lab</strong>
                                <span className="seed-priority medium">QA</span>
                            </div>
                            <p>
                                Run learning, generate test sites, inspect health score, and validate vertical readiness.
                            </p>
                        </Link>
                    </div>
                </AdminSection>

                <AdminSection
                    kicker="Launch Readiness"
                    title="Public Generation Watch"
                    description="Review guest previews that were generated but not claimed. Useful for abuse checks, conversion tracking, and cleanup."
                    action={
                        <Link to="/projects" className="project-action-btn secondary">
                            View Projects
                        </Link>
                    }
                >
                    <UnclaimedPreviewSessionsPanel />
                </AdminSection>
            </section>

            <AdminSection
                kicker="Operations"
                title="Core Platform Areas"
                description="Shortcuts for the main super-admin workspaces."
            >
                <div className="dash-rec-list">
                    <Link to="/accounts" className="dash-rec-item">
                        <div className="dash-rec-top">
                            <strong>Accounts</strong>
                            <ShieldCheck size={16} />
                        </div>
                        <p>Manage platform, agency, and client accounts.</p>
                    </Link>

                    <Link to="/projects" className="dash-rec-item">
                        <div className="dash-rec-top">
                            <strong>Projects</strong>
                            <FolderKanban size={16} />
                        </div>
                        <p>Inspect generated projects, ownership, deployment state, and CRM content.</p>
                    </Link>

                    <Link to="/leads" className="dash-rec-item">
                        <div className="dash-rec-top">
                            <strong>Leads</strong>
                            <Activity size={16} />
                        </div>
                        <p>Review captured enquiries across generated websites.</p>
                    </Link>

                    <Link to="/vertical-seeds" className="dash-rec-item">
                        <div className="dash-rec-top">
                            <strong>Vertical Intelligence</strong>
                            <DatabaseZap size={16} />
                        </div>
                        <p>Manage V2 seeds, blueprints, learning, test lab, and image readiness.</p>
                    </Link>

                    <Link to="/admin/monitoring" className="dash-rec-item">
                        <div className="dash-rec-top">
                            <strong>Generation Monitoring</strong>
                            <ShieldAlert size={16} />
                        </div>
                        <p>Review public preview attempts, quota blocks, IP activity, and bot signals.</p>
                    </Link>
                </div>
            </AdminSection>

            <AdminSection
                kicker="Danger Zone"
                title="Clear Generated Projects"
                description="Super Admin only. Removes generated/test projects, CRM module data, leads, deployment runs, local generated-site folders, and Azure generated-site folders."
                action={
                    <button
                        type="button"
                        className="project-action-btn secondary"
                        onClick={handleCleanupDryRun}
                        disabled={cleanupRunning}
                    >
                        <RefreshCw size={16} />
                        {cleanupRunning ? "Checking..." : "Preview Cleanup"}
                    </button>
                }
            >
                <div className="cleanup-danger-panel">
                    <div className="cleanup-grid">
                        <label>
                            <span>Cleanup Scope</span>
                            <select
                                value={cleanupScope}
                                onChange={(e) => {
                                    setCleanupScope(e.target.value);
                                    setCleanupPreview(null);
                                }}
                            >
                                <option value="seed-test-only">Seed test projects only</option>
                                <option value="generated-only">Generated projects</option>
                                <option value="all">All projects</option>
                            </select>
                        </label>

                        <label>
                            <span>Confirmation</span>
                            <input
                                value={cleanupConfirmation}
                                onChange={(e) => setCleanupConfirmation(e.target.value)}
                                placeholder="Type CLEAR GENERATED PROJECTS"
                            />
                        </label>
                    </div>

                    <div className="cleanup-options">
                        <label>
                            <input
                                type="checkbox"
                                checked={cleanupDeleteLocal}
                                onChange={(e) => setCleanupDeleteLocal(e.target.checked)}
                            />
                            Delete local generated-site folders
                        </label>

                        <label>
                            <input
                                type="checkbox"
                                checked={cleanupDeleteAzure}
                                onChange={(e) => setCleanupDeleteAzure(e.target.checked)}
                            />
                            Delete Azure generated-site folders
                        </label>

                        <label>
                            <input
                                type="checkbox"
                                checked={cleanupIncludeClientOwned}
                                onChange={(e) => setCleanupIncludeClientOwned(e.target.checked)}
                            />
                            Include client-owned projects
                        </label>

                        <label className="cleanup-wide">
                            <span>Orphan Azure slugs</span>
                            <textarea
                                value={cleanupOrphanSlugs}
                                onChange={(e) => setCleanupOrphanSlugs(e.target.value)}
                                placeholder="primecare-health-center&#10;urban-brew-cafe&#10;harvest-table-kitchen"
                                rows={4}
                            />
                        </label>
                    </div>

                    {cleanupPreview && (
                        <div className="cleanup-preview-box">
                            <strong>{cleanupPreview.dryRun ? "Dry Run Result" : "Cleanup Result"}</strong>

                            <div className="cleanup-preview-grid">
                                <span>Projects matched: <b>{cleanupPreview.projectsMatched || 0}</b></span>
                                <span>Projects deleted: <b>{cleanupPreview.projectsDeleted || 0}</b></span>
                                <span>Leads: <b>{cleanupPreview.leadsDeleted || 0}</b></span>
                                <span>Lead audits: <b>{cleanupPreview.leadAuditsDeleted || 0}</b></span>
                                <span>Modules: <b>{cleanupPreview.moduleDefinitionsDeleted || 0}</b></span>
                                <span>Module items: <b>{cleanupPreview.moduleItemsDeleted || 0}</b></span>
                                <span>Redeploy runs: <b>{cleanupPreview.redeployRunsDeleted || 0}</b></span>
                                <span>AI logs: <b>{cleanupPreview.aiUsageLogsDeleted || 0}</b></span>
                                <span>Local folders: <b>{cleanupPreview.localFoldersDeleted || 0}</b></span>
                                <span>Azure folders: <b>{cleanupPreview.azureFoldersDeleted || 0}</b></span>
                            </div>

                            {cleanupPreview.projectSlugs?.length > 0 && (
                                <details className="cleanup-details">
                                    <summary>Matched slugs</summary>
                                    <p>{cleanupPreview.projectSlugs.join(", ")}</p>
                                </details>
                            )}

                            {cleanupPreview.azureDeleteErrors?.length > 0 && (
                                <details className="cleanup-details error">
                                    <summary>Azure delete errors</summary>
                                    <p>{cleanupPreview.azureDeleteErrors.join(" | ")}</p>
                                </details>
                            )}

                            {cleanupPreview.localDeleteErrors?.length > 0 && (
                                <details className="cleanup-details error">
                                    <summary>Local delete errors</summary>
                                    <p>{cleanupPreview.localDeleteErrors.join(" | ")}</p>
                                </details>
                            )}
                        </div>
                    )}

                    {cleanupMessage && (
                        <div className="module-toast success">
                            {cleanupMessage}
                        </div>
                    )}

                    <button
                        type="button"
                        className="project-action-btn danger cleanup-delete-btn"
                        onClick={handleClearGeneratedProjects}
                        disabled={cleanupRunning || cleanupConfirmation !== "CLEAR GENERATED PROJECTS"}
                    >
                        <Trash2 size={16} />
                        {cleanupRunning ? "Deleting..." : "Clear Generated Projects"}
                    </button>
                </div>
            </AdminSection>
        </div>
    );
}